import PropTypes from "prop-types";

import { Box, Paper, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import { DataGrid } from "@mui/x-data-grid";

import { useAuth } from "@/features/authentication";
import { PermissionEnum } from "@/features/permissions";
import { formatNumber } from "@/features/shared";
import {
  DownstreamTransactionLinks,
  ProductVariantNameCell,
} from "@/features/ui";

export const GeneralProductSection = ({
  salesOrderGeneralProductVariants,
  warehouse,
}) => {
  const { hasPermission } = useAuth();

  return (
    <Paper sx={{ p: 3, borderRadius: 2 }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <Typography variant="subtitle1">Produk Umum</Typography>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <DataGrid
            getRowHeight={() => "auto"}
            columns={[
              {
                field: "name",
                headerName: "Produk Varian",
                flex: 3,
                renderCell: ({ row: { name, sku } }) => (
                  <ProductVariantNameCell
                    name={name}
                    sku={sku}
                    linkToStock
                    warehouseId={warehouse?.id}
                    warehouseName={warehouse?.name}
                  />
                ),
              },
              {
                field: "price",
                headerName: "@Harga",
                type: "number",
                flex: 1,
                valueFormatter: (value) => formatNumber(value),
              },
              {
                field: "quantity",
                headerName: "Dipesan",
                type: "number",
                flex: 1,
                valueFormatter: (value) => formatNumber(value),
              },
              {
                field: "deliveryOrders",
                headerName: "Surat Jalan",
                flex: 1.5,
                sortable: false,
                renderCell: ({ row: { deliveryOrders } }) => (
                  <DownstreamTransactionLinks
                    items={deliveryOrders}
                    clickable={hasPermission(PermissionEnum.READ_DELIVERY_ORDERS)}
                  />
                ),
              },
              {
                field: "salesInvoices",
                headerName: "Faktur",
                flex: 1.5,
                sortable: false,
                renderCell: ({ row: { salesInvoices } }) => (
                  <DownstreamTransactionLinks
                    items={salesInvoices}
                    clickable={hasPermission(PermissionEnum.READ_SALES_INVOICES)}
                  />
                ),
              },
              {
                field: "salesPayments",
                headerName: "Pembayaran",
                flex: 1.5,
                sortable: false,
                renderCell: ({ row: { salesPayments } }) => (
                  <DownstreamTransactionLinks
                    items={salesPayments}
                    clickable={hasPermission(PermissionEnum.READ_SALES_PAYMENTS)}
                  />
                ),
              },
              {
                field: "totalPrice",
                headerName: "Total",
                flex: 1.5,
                type: "number",
                valueGetter: (_, row) =>
                  row.quantity * row.price - row.discount,
                renderCell: ({ row }) => {
                  const { quantity, price, discount } = row;
                  const total = quantity * price - discount;
                  const hasDiscount = discount && discount > 0;

                  if (!hasDiscount) {
                    return (
                      <Typography variant="body2">
                        {formatNumber(total)}
                      </Typography>
                    );
                  }

                  const originalTotal = quantity * price;

                  return (
                    <Box sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatNumber(total)}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          textDecoration: "line-through",
                          color: "text.secondary",
                          fontSize: "0.7rem",
                        }}
                      >
                        {formatNumber(originalTotal)}
                      </Typography>
                    </Box>
                  );
                },
              },
            ]}
            rows={salesOrderGeneralProductVariants}
            initialState={{
              sorting: {
                sortModel: [{ field: "name", sort: "asc" }],
              },
            }}
            hideFooter
            disableColumnMenu
            disableRowSelectionOnClick
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

GeneralProductSection.propTypes = {
  salesOrderGeneralProductVariants: PropTypes.array.isRequired,
  warehouse: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }),
};
